import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";

/* Compact photo strip for a diary activity or diary entry — multi-upload with an
   optional title/note per batch, view, delete. Photos become selectable in "Create Travel Story". */
export function PhotoStrip({ tripId, kind, iid, photos = [], onChanged, readOnly = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const token = localStorage.getItem("token") || "";

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files.slice(0, 10 - photos.length)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (note.trim()) fd.append("title", note.trim());
        await api.post(`/diary/${tripId}/photos/${kind}/${iid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not upload photo");
      }
    }
    if (ok) { toast.success(ok > 1 ? `${ok} photos added` : "Photo added"); setNote(""); onChanged?.(); }
    setBusy(false);
  };

  const remove = async (pid) => {
    await api.delete(`/diary/${tripId}/photos/${kind}/${iid}/${pid}`);
    onChanged?.();
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`diary-photos-${kind}-${iid}`}>
      {photos.map((p) => (
        <span key={p.id} className="relative group inline-block">
          <img src={`${API}/diary/${tripId}/photos/${kind}/${iid}/${p.id}/image?auth=${token}`} alt={p.title || ""} title={p.title || ""}
            className="h-14 w-20 object-cover border border-zinc-200" loading="lazy" />
          {p.title && <span className="block text-[9px] text-muted-foreground w-20 truncate" title={p.title}>{p.title}</span>}
          {!readOnly && (
            <button onClick={() => remove(p.id)} data-testid={`diary-photo-del-${p.id}`}
              className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100">
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Photo title / note (optional)…"
            data-testid={`diary-photo-note-${kind}-${iid}`}
            className="h-14 w-40 border border-zinc-300 px-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500" />
          <button onClick={() => fileRef.current?.click()} disabled={busy || photos.length >= 10} data-testid={`diary-photo-add-${kind}-${iid}`}
            title={photos.length >= 10 ? "Up to 10 photos per item" : "Add photos"}
            className="inline-flex items-center gap-1 h-14 px-2 border border-dashed border-zinc-400 text-zinc-500 text-[11px] hover:border-indigo-500 hover:text-indigo-700 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} Photos
          </button>
        </>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid={`diary-photo-input-${kind}-${iid}`} />
    </div>
  );
}
